import { useEffect } from 'react';

/**
 * Close a modal when the user presses Escape.
 *
 * Dismissing an overlay with Escape is a baseline keyboard expectation — a keyboard-only user who
 * opens a modal otherwise has no way out except tabbing to find the close control. Before this,
 * SupportChat.jsx was the ONLY component in the app that handled Escape at all, so every
 * `.modal-overlay` trapped the user (found 2026-07-31 by the axe battery:
 * "modal accessibility: keyboard Escape closes modal").
 *
 * Listens on `keydown` during the CAPTURE phase so the handler still runs when focus sits inside an
 * input that stops propagation. `enabled` lets a caller suppress dismissal while a request is in
 * flight, so Escape can't discard a half-saved form.
 *
 * @param {() => void} onClose   invoked on Escape
 * @param {boolean}    enabled   default true; pass false to temporarily disable (e.g. while saving)
 */
export function useEscapeToClose(onClose, enabled = true) {
  useEffect(() => {
    if (!enabled || typeof onClose !== 'function') return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();   // only the topmost modal closes, not every one in a nested stack
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, enabled]);
}

export default useEscapeToClose;
